import { Route } from '@angular/router';
import { AuthGuard } from 'app/authentication/auth/guards/auth.guard';
import { AdminGuard } from 'app/authentication/auth/guards/admin.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { InitialDataResolver } from 'app/app.resolvers';
import { ModuleGuard } from 'app/authentication/auth/guards/module.guard';
import {
    NotServicesBoHostMatchGuard,
    ServicesBoHostMatchGuard,
    servicesBoClientsRedirectCanMatch,
    servicesBoRootRedirectCanMatch,
    servicesBoWildcardRedirectCanMatch,
    StoreHostMatchGuard,
    storeHostWildcardRedirectCanMatch,
    tenantAppRootRedirectCanMatch,
    tenantAppWildcardRedirectCanMatch,
} from 'app/modules/services-store/guards/services-host.guard';
import { RedirectComponent } from 'app/shared/redirect/redirect.component';

export const appRoutes: Route[] = [
    {
        path: '',
        canMatch: [StoreHostMatchGuard],
        loadChildren: () =>
            import('app/modules/services-store/services-storefront.module').then((m) => m.ServicesStorefrontModule),
    },

    {
        path: '',
        pathMatch: 'full',
        canMatch: [servicesBoRootRedirectCanMatch],
        component: RedirectComponent,
    },

    {
        path: '',
        pathMatch: 'full',
        canMatch: [tenantAppRootRedirectCanMatch],
        component: RedirectComponent,
    },
    { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'dashboard' },

    // Auth UI is centralized on the API at /auth/* (edge-proxied). No Angular auth pages.

    {
        path: 'dashboard',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: { initialData: InitialDataResolver },
        children: [
            {
                path: '',
                loadChildren: () => import('app/modules/dashboard/dashboard.module').then(m => m.DashboardModule)
            }
        ]
    },

    {
        path: 'admin',
        canMatch: [AdminGuard],
        canActivate: [AdminGuard],
        canActivateChild: [AdminGuard],
        component: LayoutComponent,
        resolve: { initialData: InitialDataResolver },
        children: [
            {
                path: '',
                loadChildren: () => import('app/modules/admin/tenant-admin.module').then(m => m.TenantAdminModule),
            },
        ]
    },

    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: { initialData: InitialDataResolver },
        children: [
            {
                path: 'profile',
                loadChildren: () => import('app/modules/profile/profile.module').then(m => m.ProfileModule)
            },
            {
                path: 'clients',
                canMatch: [servicesBoClientsRedirectCanMatch],
                component: RedirectComponent,
            },
            {
                path: 'store',
                canMatch: [ServicesBoHostMatchGuard],
                loadChildren: () =>
                    import('app/modules/services-store/services-store-admin.module').then((m) => m.ServicesStoreAdminModule),
            },
            {
                path: 'website',
                canActivate: [ModuleGuard],
                canActivateChild: [ModuleGuard],
                canMatch: [ModuleGuard],
                data: { module: 'website' },
                loadChildren: () => import('app/modules/website/website.module').then(m => m.WebsiteModule)
            },
            {
                path: 'store',
                canMatch: [NotServicesBoHostMatchGuard, ModuleGuard],
                canActivate: [ModuleGuard],
                canActivateChild: [ModuleGuard],
                data: { module: 'store' },
                loadChildren: () => import('app/modules/store-bo/store-bo.module').then(m => m.StoreBoModule)
            },
            {
                path: 'mailing',
                canActivate: [ModuleGuard],
                canActivateChild: [ModuleGuard],
                canMatch: [ModuleGuard],
                data: { module: 'mailing' },
                loadChildren: () => import('app/modules/mailing/mailing.module').then(m => m.MailingModule)
            },

            {
                path: 'hrm',
                canActivate: [ModuleGuard],
                canActivateChild: [ModuleGuard],
                canMatch: [ModuleGuard],
                data: { module: 'hrm' },
                loadChildren: () => import('app/modules/hrm/hrm.module').then(m => m.HrmModule)
            },
            {
                path: 'accounting',
                canActivate: [ModuleGuard],
                canActivateChild: [ModuleGuard],
                canMatch: [ModuleGuard],
                data: { module: 'accounting' },
                loadChildren: () => import('app/modules/accounting/accounting.module').then(m => m.AccountingModule)
            },
            {
                path: 'payroll',
                canActivate: [ModuleGuard],
                canActivateChild: [ModuleGuard],
                canMatch: [ModuleGuard],
                data: { module: 'payroll' },
                loadChildren: () => import('app/modules/payroll/payroll.module').then(m => m.PayrollModule)
            },
            {
                path: 'pms',
                canActivate: [ModuleGuard],
                canActivateChild: [ModuleGuard],
                canMatch: [ModuleGuard],
                data: { module: 'pms' },
                loadChildren: () => import('app/modules/pms/pms.module').then(m => m.PmsModule)
            },
            {
                path: 'billing',
                canActivate: [ModuleGuard],
                canActivateChild: [ModuleGuard],
                canMatch: [ModuleGuard],
                data: { module: 'billing' },
                loadChildren: () => import('app/modules/billing/billing.module').then(m => m.BillingModule)
            },
            {
                path: 'marketing',
                canActivate: [ModuleGuard],
                canActivateChild: [ModuleGuard],
                canMatch: [ModuleGuard],
                data: { module: 'marketing' },
                loadChildren: () => import('app/modules/marketing/marketing.module').then(m => m.MarketingModule)
            },
            {
                path: 'wallet',
                canActivate: [ModuleGuard],
                canActivateChild: [ModuleGuard],
                canMatch: [ModuleGuard],
                data: { module: 'wallet' },
                loadChildren: () => import('app/modules/wallet/wallet.module').then(m => m.WalletModule)
            },
            {
                path: 'wiki',
                canActivate: [ModuleGuard],
                canActivateChild: [ModuleGuard],
                canMatch: [ModuleGuard],
                data: { module: 'wiki' },
                loadChildren: () => import('app/modules/wiki/wiki.module').then(m => m.WikiModule)
            },
            {
                path: 'ai',
                canActivate: [ModuleGuard],
                canActivateChild: [ModuleGuard],
                canMatch: [ModuleGuard],
                data: { module: 'ai' },
                loadChildren: () => import('app/modules/ai/ai.module').then(m => m.AiModule)
            },
        ]
    },

    {
        path: '**',
        canMatch: [storeHostWildcardRedirectCanMatch],
        component: RedirectComponent,
    },
    {
        path: '**',
        canMatch: [servicesBoWildcardRedirectCanMatch],
        component: RedirectComponent,
    },
    {
        path: '**',
        canMatch: [tenantAppWildcardRedirectCanMatch],
        component: RedirectComponent,
    },
];
